import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import UniversityRepeatableField from "../../Frontend/components/UniversityRepeatableField";

function ApplicationFields({ applicationFields = [], customFields = [] }) {
  const [expandedJsonSections, setExpandedJsonSections] = useState({});
  const [groupOrders, setGroupOrders] = useState({});

  const toggleJsonSection = (fieldIndex) => {
    setExpandedJsonSections((prev) => ({
      ...prev,
      [fieldIndex]: !prev[fieldIndex],
    }));
  };

  const getOrderedGroups = (chunks, fieldIndex) => {
    const fieldKey = `field_${fieldIndex}`;
    const order = groupOrders[fieldKey];

    if (!order || order.length !== chunks.length) {
      // Initialize order if not exists or length mismatch
      const newOrder = chunks.map((_, index) => index);
      setGroupOrders((prev) => ({
        ...prev,
        [fieldKey]: newOrder,
      }));
      return chunks;
    }

    return order.map((index) => chunks[index]);
  };

  const renderFieldValue = (field, fieldIndex) => {
    // Handle university repeatable
    if (field.field_type === "university_repeatable") {
      return (
        <UniversityRepeatableField
          fieldIndex={0}
          field={{ label: field.field_name }}
          isRequired={false}
          displayMode={true}
          initialValue={field.field_value}
        />
      );
    }

    // Handle application_sub_fields with chunking into groups of 5
    if (
      Array.isArray(field.application_sub_fields) &&
      field.application_sub_fields.length > 0
    ) {
      return (
        <div className="w-full">
          <div className="border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => toggleJsonSection(fieldIndex)}
              className="w-full flex items-center justify-between p-2 text-sm bg-muted hover:bg-muted"
            >
              <span className="text-foreground font-semibold">Degree Details</span>
              <ChevronDown
                className={`w-3 h-3 transition-transform ${
                  expandedJsonSections[fieldIndex] ? "rotate-180" : ""
                }`}
              />
            </button>

            {expandedJsonSections[fieldIndex] && (
              <div className="p-4 bg-card space-y-4">
                {(() => {
                  const chunkSize = 5;
                  const chunks = [];

                  for (
                    let i = 0;
                    i < field.application_sub_fields.length;
                    i += chunkSize
                  ) {
                    chunks.push(
                      field.application_sub_fields.slice(i, i + chunkSize),
                    );
                  }

                  const orderedGroups = getOrderedGroups(chunks, fieldIndex);

                  return orderedGroups.map((group, displayIndex) => {
                    const originalIndex = chunks.findIndex(
                      (chunk) => chunk === group,
                    );

                    return (
                      <React.Fragment
                        key={`group_${originalIndex}_${displayIndex}`}
                      >
                        {displayIndex > 0 && (
                          <hr className="my-4 border-border" />
                        )}
                        <div className="space-y-3">
                          {group.map((subField, idx) => {
                            const { label, value } = subField;
                            if (!label || !value) return null;

                            return (
                              <div key={idx}>
                                <h5 className="text-sm font-bold text-foreground mb-1">
                                  {label}
                                </h5>
                                <p className="text-xs text-foreground bg-muted rounded-xl px-3 py-1 border">
                                  {value}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </React.Fragment>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        </div>
      );
    }

    // Rich text fallback
    if (
      customFields.find(
        (customField) =>
          customField.label === field.field_name &&
          ["text", "textarea", "wysiwyg"].includes(customField.field_type),
      )
    ) {
      return (
        <div className="bg-muted text-foreground px-3 py-1 rounded-xl text-xs border border-border inline-block">
          <div dangerouslySetInnerHTML={{ __html: field.field_value }}></div>
        </div>
      );
    }

    // Simple text (split commas)
    if (field.field_type === "text") {
      return field.field_value.split(",").map((value, idx) => (
        <div
          key={idx}
          className="bg-muted text-foreground px-3 py-1 rounded-xl text-xs border border-border inline-block"
        >
          {value.trim()}
        </div>
      ));
    }

    // Fallback
    return (
      <div className="bg-muted text-foreground px-3 py-1 rounded-xl text-xs border border-border inline-block">
        {field.field_value || "N/A"}
      </div>
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="space-y-4">
        {applicationFields
          ?.filter(
            (field) =>
              field.form_type === "personal" ||
              field.field_type === "university_repeatable",
          )
          .map((field, index) => (
            <div key={index}>
              <h4 className="text-foreground font-semibold mb-2 capitalize">
                {field.field_name}
              </h4>
              <div className="flex gap-2 flex-wrap">
                {renderFieldValue(field, index)}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

export default ApplicationFields;
